﻿import { useEffect, useMemo, useRef } from 'react';
import { AlertCircle, MessageSquareDashed } from 'lucide-react';
import MessageBubble from './MessageBubble';

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/** Same calendar day in the viewer's local time. */
function sameDay(a, b) {
  return (
    a.getFullYear() === b.getFullYear()
    && a.getMonth() === b.getMonth()
    && a.getDate() === b.getDate()
  );
}

/** "Today" / "Yesterday" / "12 Oct" (the year only when it is not this year). */
function dayLabel(value) {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '';

  const now = new Date();
  if (sameDay(date, now)) return 'Today';

  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (sameDay(date, yesterday)) return 'Yesterday';

  return date.toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    year: date.getFullYear() === now.getFullYear() ? undefined : 'numeric',
  });
}

/**
 * Scrollable message list.
 *
 * Auto-scrolls to the newest message: instantly when the conversation changes
 * (jumping to the bottom of an existing history) and smoothly when a message
 * arrives, which is what makes sending feel responsive.
 */
export default function MessageThread({
  conversationId,
  participantName,
  messages,
  status,
  error,
  hasMore,
  onLoadOlder,
  onOpenImage,
  partnerTyping = false,
}) {
  const containerRef = useRef(null);
  const previousConversation = useRef(conversationId);
  const previousCount = useRef(messages.length);
  // True while the newest message is on screen; the keyboard/orientation
  // handlers below only re-pin the view in that case, so scrolling back through
  // history is never yanked away.
  const nearBottom = useRef(true);

  /** Only the last message you sent carries the sent/seen indicator. */
  const lastSentId = useMemo(() => {
    for (let index = messages.length - 1; index >= 0; index -= 1) {
      if (messages[index].mine) return messages[index].id;
    }
    return null;
  }, [messages]);

  useEffect(() => {
    const element = containerRef.current;
    if (!element) return;

    const switchedConversation = previousConversation.current !== conversationId;
    const grew = messages.length > previousCount.current;
    previousConversation.current = conversationId;
    previousCount.current = messages.length;

    if (!switchedConversation && !grew) return;

    nearBottom.current = true;
    element.scrollTo({
      top: element.scrollHeight,
      behavior: switchedConversation || prefersReducedMotion() ? 'auto' : 'smooth',
    });
  }, [conversationId, messages.length]);

  // The typing row grows the list without a new message; keep it in view when
  // the user is already reading the newest part of the thread.
  useEffect(() => {
    if (!partnerTyping || !nearBottom.current) return;
    const element = containerRef.current;
    element?.scrollTo({ top: element.scrollHeight, behavior: 'smooth' });
  }, [partnerTyping]);

  /**
   * Mobile keyboards and rotation change the visible viewport without changing
   * the message list, so nothing above would fire. When the user is at the
   * bottom (the normal case while typing) the newest message is re-pinned; when
   * they have scrolled up into history, their position is left alone.
   */
  useEffect(() => {
    const keepNewestVisible = () => {
      const element = containerRef.current;
      if (!element || !nearBottom.current) return;
      element.scrollTo({ top: element.scrollHeight, behavior: 'auto' });
    };

    const viewport = window.visualViewport;
    viewport?.addEventListener('resize', keepNewestVisible);
    window.addEventListener('orientationchange', keepNewestVisible);

    return () => {
      viewport?.removeEventListener('resize', keepNewestVisible);
      window.removeEventListener('orientationchange', keepNewestVisible);
    };
  }, []);

  const handleScroll = (event) => {
    const element = event.currentTarget;
    nearBottom.current =
      element.scrollHeight - element.scrollTop - element.clientHeight < 140;
  };

  if (status === 'error') {
    return (
      <div className="flex min-h-0 flex-1 items-center justify-center px-6 py-10">
        <div className="text-center">
          <span className="mx-auto grid h-12 w-12 place-items-center rounded-2xl bg-surface-3 text-rose-500">
            <AlertCircle size={20} />
          </span>
          <p className="mt-3 text-[13px] font-medium text-ink">Couldnâ€™t load this conversation</p>
          <p className="mt-1 text-[11.5px] text-ink-3">{error?.message}</p>
        </div>
      </div>
    );
  }

  if (status === 'loading' && messages.length === 0) {
    return (
      <div className="flex min-h-0 flex-1 flex-col justify-end gap-3 px-5 py-4" role="status">
        <span className="sr-only">Loading messagesâ€¦</span>
        {[68, 45, 80, 52].map((width, index) => (
          <span
            key={index}
            style={{ width: `${width}%` }}
            className={`h-9 animate-pulse rounded-2xl bg-surface-3 ${
              index % 2 === 0 ? 'self-start' : 'self-end'
            }`}
          />
        ))}
      </div>
    );
  }

  if (messages.length === 0) {
    return (
      <div className="flex min-h-0 flex-1 items-center justify-center px-6 py-10">
        <div className="text-center">
          <span className="mx-auto grid h-12 w-12 place-items-center rounded-2xl bg-surface-3 text-ink-3">
            <MessageSquareDashed size={20} />
          </span>
          <p className="mt-3 text-[13px] font-medium text-ink">No messages yet - say hello!</p>
          <p className="mt-1 text-[11.5px] text-ink-3">
            Your conversation with {participantName} starts here.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      onScroll={handleScroll}
      className="z2-scroll min-h-0 flex-1 overflow-y-auto px-3 py-3 md:px-5 md:py-4"
      aria-label={`Messages with ${participantName}`}
      role="log"
    >
      {hasMore && (
        <div className="mb-3 flex justify-center">
          <button
            type="button"
            onClick={onLoadOlder}
            className="rounded-lg border border-line px-3 py-1.5 text-[11.5px] font-medium text-ink-2 transition hover:border-brand-300 hover:bg-brand-soft hover:text-accent"
          >
            Load earlier messages
          </button>
        </div>
      )}

      <ul className="space-y-2.5">
        {messages.map((message, index) => {
          // A day divider whenever the calendar day changes, so a long thread
          // reads like a conversation instead of an endless wall of bubbles.
          const previous = index > 0 ? messages[index - 1] : null;
          const currentAt = message.createdAt ?? message.sentAt ?? message.timestamp;
          const previousAt = previous?.createdAt ?? previous?.sentAt ?? previous?.timestamp;
          const showDay =
            Boolean(currentAt)
            && (!previousAt || dayLabel(currentAt) !== dayLabel(previousAt));

          return (
            <li key={message.id}>
              {showDay && (
                <div className="mb-2 mt-1 flex justify-center">
                  <span className="rounded-full bg-surface-3 px-2.5 py-0.5 text-[10.5px] font-semibold uppercase tracking-wide text-ink-3">
                    {dayLabel(currentAt)}
                  </span>
                </div>
              )}
              <MessageBubble
                message={message}
                isLastSent={message.id === lastSentId}
                onOpenImage={onOpenImage}
              />
            </li>
          );
        })}

        {partnerTyping && (
          <li className="flex items-center gap-2 pl-1 pt-1" aria-live="polite">
            <span className="flex items-center gap-1 rounded-2xl bg-surface-3 px-2.5 py-1.5">
              {[0, 1, 2].map((dot) => (
                <span
                  key={dot}
                  className="h-1.5 w-1.5 animate-bounce rounded-full bg-ink-3"
                  style={{ animationDelay: `${dot * 120}ms`, animationDuration: '900ms' }}
                />
              ))}
            </span>
            <span className="text-[11px] text-ink-3">{participantName} is typingâ€¦</span>
          </li>
        )}
      </ul>
    </div>
  );
}
